import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import Layout from './components/Layout';
import CookieConsent from './components/CookieConsent';
import HomePage from './pages/HomePage';
import ExplorePage from './pages/ExplorePage';
import AttractionDetail from './pages/AttractionDetail';
import TownsPage from './pages/TownsPage';
import TownDetail from './pages/TownDetail';
import EventsPage from './pages/EventsPage';
import EventDetail from './pages/EventDetail';
import PackagesPage from './pages/PackagesPage';
import CompetitionPage from './pages/CompetitionPage';
import PlanMyDay from './pages/PlanMyDay';
import HalfDaysPage from './pages/HalfDaysPage';
import HalfDayDetail from './pages/HalfDayDetail';
import DigestPage from './pages/DigestPage';
import ItineraryBuilder from './pages/ItineraryBuilder';
import SharedItinerary from './pages/SharedItinerary';

import AdminLogin from './admin/AdminLogin';
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import AdminAttractions from './admin/AdminAttractions';
import AdminEvents from './admin/AdminEvents';
import AdminMembers from './admin/AdminMembers';
import AdminPackages from './admin/AdminPackages';
import AdminDigest from './admin/AdminDigest';
import AdminSubscribers from './admin/AdminSubscribers';
import AdminCosts from './admin/AdminCosts';
import AdminNewsletter from './admin/AdminNewsletter';
import AdminScraper from './admin/AdminScraper';
import AdminAwards from './admin/AdminAwards';
import AdminCountyLocations from './admin/AdminCountyLocations';
import AdminMedia from './admin/AdminMedia';
import AdminSeo from './admin/AdminSeo';
import AdminStats from './admin/AdminStats';
import AdminInspirations from './admin/AdminInspirations';
import AdminNotifications from './admin/AdminNotifications';
import AdminAdmins from './admin/AdminAdmins';
import AdminAuditLog from './admin/AdminAuditLog';
import AdminSentinel from './admin/AdminSentinel';
import AcceptInvite from './admin/AcceptInvite';
import TradePartnersPage from './pages/TradePartnersPage';
import TradePageView from './pages/TradePageView';
import ServicesPage from './pages/ServicesPage';
import AwardsTicketsPage from './pages/AwardsTicketsPage';
import AwardsVideoUploadPage from './pages/AwardsVideoUploadPage';
import AdminFinalists from './admin/AdminFinalists';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import PartnerProtected from './components/PartnerProtected';
import PartnerSetPassword from './pages/PartnerSetPassword';
import AdminPartnerUsers from './admin/AdminPartnerUsers';
import AdminTradePages from './admin/AdminTradePages';
import AdminPartnerResources from './admin/AdminPartnerResources';
import AdminPayments from './admin/AdminPayments';
import AdminAttractionEdits from './admin/AdminAttractionEdits';
import AdminGuide from './admin/AdminGuide';
import AdminLester from './admin/AdminLester';
import ClaimVenuePage from './pages/ClaimVenuePage';
import PartnerMyListingPage from './pages/PartnerMyListingPage';
import PartnerGuidePage from './pages/PartnerGuidePage';
import LegacyRedirect from './components/LegacyRedirect';
import CanonicalTag from './components/CanonicalTag';
import NotFoundPage from './pages/NotFoundPage';
import AdminCopy from './admin/AdminCopy';
import AwardsPage from './pages/AwardsPage';
import AwardsVotePage from './pages/AwardsVotePage';
import AwardsCriteriaPage from './pages/AwardsCriteriaPage';
import AwardsYearPage from './pages/AwardsYearPage';
import { PrivacyPage, CookiesPage, TermsPage } from './pages/LegalPages';
import AccommodationPage from './pages/AccommodationPage';
import AboutPage from './pages/AboutPage';
import AccessibilityPage from './pages/AccessibilityPage';
import AccessibleDaysOutPage from './pages/AccessibleDaysOutPage';
import { useRouteTracker } from './lib/tracker';

function RouteTracker() { useRouteTracker(); return null; }

/** Canonicalise uppercase URL variants (/Explore → /explore) so analytics and
 *  SEO see one page. Token-bearing routes are exempt (tokens are case-sensitive). */
const CASE_EXEMPT_RX = /^\/(itinerary\/|claim\/|partners\/set-password\/|admin)/i;
function CaseRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  React.useEffect(() => {
    const p = location.pathname;
    if (/[A-Z]/.test(p) && !CASE_EXEMPT_RX.test(p)) {
      navigate(p.toLowerCase() + location.search + location.hash, { replace: true });
    }
  }, [location, navigate]);
  return null;
}

/** Reset scroll on every navigation. Without this, clicking the logo from
 *  mid-way down a long detail page leaves you scrolled into the homepage. */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (hash) {
      // Content loads async, so retry briefly until the anchor exists.
      let tries = 0;
      const t = setInterval(() => {
        const el = document.getElementById(hash.slice(1));
        tries += 1;
        if (el) { el.scrollIntoView({ behavior: 'smooth' }); clearInterval(t); }
        else if (tries > 20) clearInterval(t);
      }, 100);
      return () => clearInterval(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function App() {
  // Lenis smooth-scroll was reported to "stop part way" on long pages — disable
  // until we can plumb resize() calls into image loads + route changes.
  // Native scrolling is reliable and accessible; CSS `scroll-behavior: smooth`
  // handles anchor links.

  return (
    <HelmetProvider>
    <BrowserRouter>
      <RouteTracker />
      <CaseRedirect />
      <ScrollToTop />
      <CookieConsent />
      <LegacyRedirect />
      <CanonicalTag />
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/explore/:slug" element={<AttractionDetail />} />
          <Route path="/towns" element={<TownsPage />} />
          <Route path="/towns/:slug" element={<TownDetail />} />
          <Route path="/accommodation" element={<AccommodationPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/archive" element={<Navigate to="/events" replace />} />
          <Route path="/events/:slug" element={<EventDetail />} />
          <Route path="/packages" element={<PackagesPage />} />
          <Route path="/competition" element={<CompetitionPage />} />
          <Route path="/plan" element={<PlanMyDay />} />
          <Route path="/half-days" element={<HalfDaysPage />} />
          <Route path="/half-days/:slug" element={<HalfDayDetail />} />
          <Route path="/itinerary" element={<ItineraryBuilder />} />
          <Route path="/itinerary/:token" element={<SharedItinerary />} />
          <Route path="/digest" element={<DigestPage />} />
          <Route path="/awards" element={<AwardsPage />} />
          <Route path="/awards/vote" element={<AwardsVotePage />} />
          <Route path="/awards/criteria" element={<AwardsCriteriaPage />} />
          {/* Legacy URL — permanent redirect to /awards (platform serves SPA, so this is router-level) */}
          <Route path="/trade/tourism-awards-tcs" element={<Navigate to="/awards" replace />} />
          {/* Set-password page MUST stay OUTSIDE the PartnerProtected gate —
              new partners haven't logged in yet when they hit this link. */}
          <Route path="/partners/set-password/:token" element={<PartnerSetPassword />} />
          <Route path="/partners" element={<PartnerProtected><TradePartnersPage /></PartnerProtected>} />
          <Route path="/partners/my-listing" element={<PartnerProtected><PartnerMyListingPage /></PartnerProtected>} />
          <Route path="/partners/guide" element={<PartnerProtected><PartnerGuidePage /></PartnerProtected>} />
          <Route path="/trade" element={<PartnerProtected><TradePartnersPage /></PartnerProtected>} />
          <Route path="/partners/:slug" element={<PartnerProtected><TradePageView /></PartnerProtected>} />
          <Route path="/claim/:token" element={<ClaimVenuePage />} />
          <Route path="/awards/:year" element={<AwardsYearPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/accessibility" element={<AccessibilityPage />} />
          <Route path="/accessible-days-out" element={<AccessibleDaysOutPage />} />
          <Route path="/awards/tickets" element={<AwardsTicketsPage />} />
          <Route path="/awards/finalist-video-upload" element={<AwardsVideoUploadPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/invite/:token" element={<AcceptInvite />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="guide" element={<AdminGuide />} />
          <Route path="lester" element={<AdminLester />} />
          <Route path="attractions" element={<AdminAttractions />} />
          <Route path="events" element={<AdminEvents />} />
          <Route path="scraper" element={<AdminScraper />} />
          <Route path="members" element={<AdminMembers />} />
          <Route path="packages" element={<AdminPackages />} />
          <Route path="awards" element={<AdminAwards />} />
          <Route path="finalists" element={<AdminFinalists />} />
          <Route path="county-locations" element={<AdminCountyLocations />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="seo" element={<AdminSeo />} />
          <Route path="copy" element={<AdminCopy />} />
          <Route path="stats" element={<AdminStats />} />
          <Route path="inspirations" element={<AdminInspirations />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="digest" element={<AdminDigest />} />
          <Route path="newsletter" element={<AdminNewsletter />} />
          <Route path="subscribers" element={<AdminSubscribers />} />
          <Route path="costs" element={<AdminCosts />} />
          <Route path="admins" element={<AdminAdmins />} />
          <Route path="partner-applications" element={<AdminPartnerUsers />} />
          <Route path="trade-pages" element={<AdminTradePages />} />
          <Route path="trade-pages/:slug" element={<AdminTradePages />} />
          <Route path="partner-resources" element={<AdminPartnerResources />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="attraction-edits" element={<AdminAttractionEdits />} />
          <Route path="audit-log" element={<AdminAuditLog />} />
          <Route path="sentinel" element={<AdminSentinel />} />
        </Route>
      </Routes>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
